import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { getAllEarnProjects } from '../api/projects';
import '../styles/ICOListing.css';

const API_URL = 'https://onlineico.com/api';

const rankColor = (rank) => {
  if (rank === 1) return '#F59E0B';
  if (rank === 2) return '#94a3b8';
  if (rank === 3) return '#CD7F32';
  return '#475569';
};

const rankEmoji = (rank) => {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
};

export default function ParticipantDashboard() {
  const { user, token, authLoading, logoutUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('explorer');

  // Explorer
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Mes Quests
  const [myStats, setMyStats] = useState([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [loadingStats, setLoadingStats] = useState(false);

  // Profil
  const [profile, setProfile] = useState(null);
  const [walletEvm, setWalletEvm] = useState('');
  const [walletSolana, setWalletSolana] = useState('');
  const [savingWallet, setSavingWallet] = useState(false);
  const [profileMsg, setProfileMsg] = useState('');
  const [profileMsgType, setProfileMsgType] = useState('success');

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/connexion'); return; }
    if (user.role === 'admin') { navigate('/admin'); return; }
    if (user.role === 'client') { navigate('/mon-compte'); return; }
    loadProjects();
  }, [user, authLoading]);

  useEffect(() => {
    if (tab === 'quests' && myStats.length === 0) loadMyStats();
    if (tab === 'profil' && !profile) loadProfile();
  }, [tab]);

  async function loadProjects() {
    setLoadingProjects(true);
    const data = await getAllEarnProjects();
    setProjects(Array.isArray(data) ? data : []);
    setLoadingProjects(false);
  }

  async function loadMyStats() {
    setLoadingStats(true);
    const res = await fetch(`${API_URL}/auth/participant-stats.php`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    setMyStats(data.projects || []);
    setTotalPoints(data.total_points || 0);
    setLoadingStats(false);
  }

  async function loadProfile() {
    const res = await fetch(`${API_URL}/auth/profile.php`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    setProfile(data);
    setWalletEvm(data.wallet_evm || '');
    setWalletSolana(data.wallet_solana || '');
  }

  async function saveWallet() {
    setSavingWallet(true);
    setProfileMsg('');
    const res = await fetch(`${API_URL}/auth/profile.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ wallet_evm: walletEvm, wallet_solana: walletSolana })
    });
    const data = await res.json();
    if (data.success) {
      setProfileMsg('Wallet saved successfully!');
      setProfileMsgType('success');
    } else {
      setProfileMsg(data.error || 'Error saving wallet');
      setProfileMsgType('error');
    }
    setSavingWallet(false);
  }

  const tabs = [
    { key: 'explorer', label: '🔍 Explorer' },
    { key: 'quests',   label: '🎯 My Quests' },
    { key: 'profil',   label: '👤 Profile' },
  ];

  return (
    <div className="app-wrap">
      <Navbar />

      {/* HEADER */}
      <div style={{
        maxWidth: '900px', margin: '40px auto 0', padding: '0 1.5rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
      }}>
        <div>
          <div style={{ fontSize: '1.5rem', fontFamily: 'Syne, sans-serif', fontWeight: '800', color: '#fff' }}>
            👋 Hey, {user?.username}
          </div>
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '4px' }}>Participant Dashboard</div>
        </div>
      </div>

      {/* TABS */}
      <div style={{ maxWidth: '900px', margin: '24px auto 0', padding: '0 1.5rem' }}>
        <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: '0' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '10px 18px',
              fontSize: '0.88rem', fontWeight: tab === t.key ? '700' : '500',
              color: tab === t.key ? '#a78bfa' : '#64748b',
              borderBottom: tab === t.key ? '2px solid #8B5CF6' : '2px solid transparent',
              transition: 'all 0.2s'
            }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* CONTENU */}
      <div style={{ maxWidth: '900px', margin: '30px auto 60px', padding: '0 1.5rem' }}>

        {/* ── EXPLORER ── */}
        {tab === 'explorer' && (
          <>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '20px' }}>
              {projects.length} project{projects.length !== 1 ? 's' : ''} with active quests
            </div>

            {loadingProjects ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>
                {[...Array(6)].map((_, i) => (
                  <div key={i} style={{
                    background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '16px', padding: '18px', height: '110px',
                    animation: 'shimmer 1.5s infinite'
                  }} />
                ))}
              </div>
            ) : projects.length === 0 ? (
              <div style={{ color: '#64748b', textAlign: 'center', padding: '60px 0' }}>No active quests at the moment.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>
                {projects.map(p => (
                  <div key={p.id} onClick={() => navigate(`/earn/${p.slug}`)} style={{
                    background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '16px', padding: '16px', cursor: 'pointer',
                    transition: 'all 0.25s', display: 'flex', flexDirection: 'column', gap: '12px'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(139,92,246,0.4)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {p.logo_url
                        ? <img src={p.logo_url} alt={p.name} style={{ width: '42px', height: '42px', borderRadius: '10px', objectFit: 'cover' }} />
                        : <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#fff', fontSize: '0.85rem' }}>
                            {p.name.substring(0, 2).toUpperCase()}
                          </div>
                      }
                      <div>
                        <div style={{ fontWeight: '700', color: '#fff', fontSize: '0.92rem' }}>{p.name}</div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>{p.ticker} · {p.chain}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '0.78rem', color: '#a78bfa' }}>
                        🎯 {p.quest_count} quest{p.quest_count !== 1 ? 's' : ''}
                      </div>
                      {p.total_reward > 0 && (
                        <div style={{ fontSize: '0.78rem', color: '#34D399', fontWeight: '700' }}>
                          💰 ${p.total_reward}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── MES QUESTS ── */}
        {tab === 'quests' && (
          <>
            {totalPoints > 0 && (
              <div style={{
                background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)',
                borderRadius: '14px', padding: '16px 20px', marginBottom: '24px',
                display: 'flex', alignItems: 'center', gap: '16px'
              }}>
                <div style={{ fontSize: '2rem' }}>⭐</div>
                <div>
                  <div style={{ fontSize: '1.4rem', fontWeight: '800', color: '#a78bfa', fontFamily: 'Syne, sans-serif' }}>{totalPoints} pts</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Total points earned across all projects</div>
                </div>
              </div>
            )}

            {loadingStats ? (
              <div style={{ color: '#64748b', textAlign: 'center', padding: '40px 0' }}>Loading...</div>
            ) : myStats.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🎯</div>
                <div style={{ color: '#94a3b8', fontSize: '0.95rem', marginBottom: '16px' }}>You haven't participated in any quests yet.</div>
                <button onClick={() => setTab('explorer')} style={{
                  background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.4)',
                  color: '#a78bfa', borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600'
                }}>
                  Explore quests →
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {myStats.map((p, i) => (
                  <div key={p.id} onClick={() => navigate(`/earn/${p.slug}`)} style={{
                    background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)',
                    borderRadius: '16px', padding: '16px 20px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '16px', transition: 'all 0.2s'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(139,92,246,0.4)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; }}
                  >
                    {/* Rang */}
                    <div style={{
                      minWidth: '44px', height: '44px', borderRadius: '12px',
                      background: `rgba(${p.my_rank === 1 ? '245,158,11' : p.my_rank === 2 ? '148,163,184' : p.my_rank === 3 ? '205,127,50' : '71,85,105'},0.15)`,
                      border: `1px solid rgba(${p.my_rank === 1 ? '245,158,11' : p.my_rank === 2 ? '148,163,184' : p.my_rank === 3 ? '205,127,50' : '71,85,105'},0.3)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: p.my_rank <= 3 ? '1.3rem' : '0.85rem',
                      fontWeight: '700', color: rankColor(p.my_rank)
                    }}>
                      {rankEmoji(p.my_rank)}
                    </div>

                    {/* Logo */}
                    {p.logo_url
                      ? <img src={p.logo_url} alt={p.name} style={{ width: '40px', height: '40px', borderRadius: '10px', objectFit: 'cover' }} />
                      : <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', color: '#fff', fontSize: '0.8rem' }}>
                          {p.name.substring(0, 2).toUpperCase()}
                        </div>
                    }

                    {/* Infos */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '700', color: '#fff', fontSize: '0.92rem' }}>{p.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px', display: 'flex', gap: '10px' }}>
                        <span style={{ color: '#34D399' }}>✅ {p.completed_quests} completed</span>
                        {p.pending_quests > 0 && <span style={{ color: '#F59E0B' }}>⏳ {p.pending_quests} pending</span>}
                        {p.rejected_quests > 0 && <span style={{ color: '#EF4444' }}>❌ {p.rejected_quests} rejected</span>}
                      </div>
                    </div>

                    {/* Points */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: '800', color: '#a78bfa', fontFamily: 'Syne, sans-serif' }}>{p.my_points}</div>
                      <div style={{ fontSize: '0.68rem', color: '#64748b' }}>points</div>
                    </div>

                    <div style={{ color: '#475569', fontSize: '0.8rem' }}>→</div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── PROFIL ── */}
        {tab === 'profil' && (
          <div style={{ maxWidth: '520px' }}>
            {!profile ? (
              <div style={{ color: '#64748b' }}>Loading...</div>
            ) : (
              <>
                {/* Carte stats */}
                <div style={{
                  background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: '20px', padding: '24px', marginBottom: '24px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                    <div style={{
                      width: '56px', height: '56px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: '800', color: '#fff', fontSize: '1.2rem', fontFamily: 'Syne, sans-serif'
                    }}>
                      {profile.username?.substring(0, 1).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: '700', color: '#fff', fontSize: '1rem' }}>{profile.username}</div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{profile.email}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '20px', padding: '4px 12px', fontSize: '0.72rem', color: '#a78bfa', fontWeight: '700' }}>
                      Participant
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div style={{
                      flex: 1, background: 'rgba(255,255,255,0.03)', borderRadius: '12px',
                      padding: '14px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#a78bfa', fontFamily: 'Syne, sans-serif' }}>{profile.total_points}</div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>Total points</div>
                    </div>
                    <div style={{
                      flex: 1, background: 'rgba(255,255,255,0.03)', borderRadius: '12px',
                      padding: '14px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)'
                    }}>
                      <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#34D399', fontFamily: 'Syne, sans-serif' }}>{profile.project_count}</div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '2px' }}>Projects</div>
                    </div>
                  </div>
                </div>

                {/* Wallet */}
                <div style={{
                  background: 'rgba(10,15,30,0.6)', border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: '20px', padding: '24px'
                }}>
                  <div style={{ fontWeight: '700', color: '#fff', marginBottom: '6px', fontSize: '0.95rem' }}>💳 Wallet Addresses</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '18px' }}>
                    Add your wallet addresses to receive quest rewards.
                  </div>

                  {/* EVM */}
                  <div style={{ marginBottom: '14px' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#94a3b8', marginBottom: '6px' }}>
                      🔷 EVM — ETH, BSC, Polygon, Avalanche...
                    </div>
                    <input
                      value={walletEvm}
                      onChange={e => setWalletEvm(e.target.value)}
                      placeholder="0x..."
                      style={{
                        width: '100%', background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
                        padding: '12px 14px', color: '#fff', fontSize: '0.85rem',
                        outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace'
                      }}
                    />
                  </div>

                  {/* Solana */}
                  <div style={{ marginBottom: '4px' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#94a3b8', marginBottom: '6px' }}>
                      🟣 Solana
                    </div>
                    <input
                      value={walletSolana}
                      onChange={e => setWalletSolana(e.target.value)}
                      placeholder="Solana address..."
                      style={{
                        width: '100%', background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
                        padding: '12px 14px', color: '#fff', fontSize: '0.85rem',
                        outline: 'none', boxSizing: 'border-box', fontFamily: 'monospace'
                      }}
                    />
                  </div>

                  {profileMsg && (
                    <div style={{
                      marginTop: '10px', fontSize: '0.82rem', padding: '8px 12px', borderRadius: '8px',
                      background: profileMsgType === 'success' ? 'rgba(52,211,153,0.1)' : 'rgba(239,68,68,0.1)',
                      color: profileMsgType === 'success' ? '#34D399' : '#f87171',
                      border: `1px solid ${profileMsgType === 'success' ? 'rgba(52,211,153,0.3)' : 'rgba(239,68,68,0.3)'}`
                    }}>
                      {profileMsg}
                    </div>
                  )}

                  <button onClick={saveWallet} disabled={savingWallet} style={{
                    marginTop: '14px', width: '100%', padding: '12px',
                    background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
                    border: 'none', borderRadius: '12px', color: '#fff',
                    fontWeight: '700', fontSize: '0.9rem', cursor: 'pointer',
                    opacity: savingWallet ? 0.6 : 1
                  }}>
                    {savingWallet ? 'Saving...' : 'Save wallet'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
